import React, { useMemo } from 'react';

import { AnimatePresence } from 'framer-motion';

import {
    FilterDropdown,
    type FilterDropdownProps,
    FilterLayoutToggle,
    FiltersBar,
    FiltersShowAction,
} from '@pkg/components/filters';
import { FieldDirection, FieldSearch, type FieldSearchProps } from '@pkg/components/forms';
import { useAppSelector } from '@pkg/hooks';

import { type TempNftField, useNFTsFiltersStore } from '@app/store/zustand/profile-page/useNFTsFiltersStore.ts';
import { useNFTsPageStore } from '@app/store/zustand/profile-page/useNFTsPageStore.ts';

import { NftFiltersReport } from '../NftFiltersReport/NftFiltersReport.tsx';

const SORT_FIELD_OPTIONS: FilterDropdownProps['options'] = [
    { id: 'date', label: 'Date added' },
    // TODO: Uncomment these fields once we have BE support.
    // { id: 'price', label: 'List price' },
    // { id: 'rarity', label: 'Rarity' },
];

function assertNftField(value = ''): value is TempNftField {
    // Not sure how else to avoid this for passing to <FilterDropdown />.
    return Boolean(value === 'date' || value === 'price' || value === 'rarity');
}

export interface NftFiltersPrimaryDesktopProps {
    resultsCount?: number;
}

export function NftFiltersPrimaryDesktop({ resultsCount = 0 }: NftFiltersPrimaryDesktopProps) {
    const marketplaceV1Enabled = useAppSelector((state) => state.featureFlags.features.marketplaceV1Enabled);

    const { isListView, showFilters, setIsListView, setShowFilters } = useNFTsPageStore();

    const currentFilters = useNFTsFiltersStore((state) => state.currentFilters);
    const search = useNFTsFiltersStore((state) => state.search);
    const sortField = useNFTsFiltersStore((state) => state.sortField);
    const sortDirection = useNFTsFiltersStore((state) => state.sortDirection);
    const setSearch = useNFTsFiltersStore((state) => state.setSearch);
    const setSortField = useNFTsFiltersStore((state) => state.setSortField);
    const setSortDirection = useNFTsFiltersStore((state) => state.setSortDirection);

    // TODO: Is it beneficial to use a debounce / useDeferredValue?
    const handleSearch: FieldSearchProps['onChange'] = (newValue = '') => {
        const stringified = newValue.toString();
        setSearch(stringified);
    };

    function handleSortFilterSelect(id: string) {
        if (assertNftField(id)) setSortField(id);
    }

    const showReport = useMemo(() => {
        return Boolean(currentFilters.total);
    }, [currentFilters]);

    const toggleGridMarkup = marketplaceV1Enabled ? (
        <FilterLayoutToggle selected={isListView} onSelected={setIsListView} />
    ) : null;

    const reportMarkup = showReport ? (
        <NftFiltersReport key="NftFiltersReport-Desktop" resultsCount={resultsCount} />
    ) : null;

    return (
        <FiltersBar>
            <FiltersBar.Layout>
                <FiltersBar.Group>
                    <FiltersShowAction pressed={showFilters} onToggle={setShowFilters} />
                    {toggleGridMarkup}
                </FiltersBar.Group>

                <FiltersBar.SearchWrapper>
                    <FieldSearch value={search} placeholder="Search collection or token" onChange={handleSearch} />
                </FiltersBar.SearchWrapper>

                {/* TODO: Where do these controls go on `mobile`? */}
                <FiltersBar.Group>
                    <FilterDropdown
                        options={SORT_FIELD_OPTIONS}
                        selectedId={sortField}
                        onSelectOption={handleSortFilterSelect}
                    />

                    <FieldDirection direction={sortDirection} onToggle={setSortDirection} />
                </FiltersBar.Group>
            </FiltersBar.Layout>

            <AnimatePresence>{reportMarkup}</AnimatePresence>
        </FiltersBar>
    );
}
